import classnames from 'classnames';
import Cropperjs from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import LoadingIndicator from 'v2/common/LoadingIndicator/LoadingIndicator';
import {
  base64StringtoFile,
  getRoundedCanvas,
  getUrlFromFile,
} from 'v2/utils/fileUtils';
import uploadFileIcon from '../icons/uploadFile.svg';
import styles from './cropper.module.scss';

interface CropperProps {
  fileUrl?: string;
  onCrop: (file: File) => void;
  rounded?: boolean;
}

const ALLOWED_FILES = ['image/jpeg', 'image/png'];

const ZOOM_STEP = 1;
const ZOOM_RESET_VAL = -1000;

const Cropper = ({
  fileUrl: savedFileUrl,
  onCrop,
  rounded = true,
}: CropperProps) => {
  const [isCropperReady, setIsCropperReady] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(savedFileUrl || '');
  const cropperContainer = useRef(null);
  const cropperInstance = useRef<Cropperjs>();
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles && ALLOWED_FILES.includes(acceptedFiles[0]?.type)) {
      setNewFile(acceptedFiles[0]);
      getUrlFromFile(acceptedFiles[0]).then(setFileUrl);
    } else {
      toast.error(`File not supported.`);
    }
  };
  const { getInputProps, getRootProps } = useDropzone({
    accept: ALLOWED_FILES.join(', '),
    multiple: false,
    noKeyboard: true,
    onDrop,
  });

  const onZoomChange = (newZoom: number) => {
    setZoom(newZoom);
    const zomStep = newZoom <= 0 ? ZOOM_RESET_VAL : 0.25 * (newZoom - zoom);
    cropperInstance?.current?.zoom(zomStep);
  };

  const buildCropper = () => {
    const image = cropperContainer.current as unknown as HTMLImageElement;
    cropperInstance.current = new Cropperjs(image, {
      aspectRatio: 1,
      cropend: () => debouncedFinishCrop(),
      ready: () => {
        debouncedFinishCrop();
        setIsCropperReady(true);
        cropperInstance.current?.zoom(ZOOM_RESET_VAL);
      },
      responsive: true,
      viewMode: 1,
      zoom: () => debouncedFinishCrop(),
    });
  };

  useEffect(() => {
    if (fileUrl) {
      cropperInstance.current?.destroy();
      buildCropper();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropperContainer.current, fileUrl]);

  const debouncedFinishCrop = React.useCallback(
    debounce(() => {
      finishCrop();
    }, 500),
    [newFile]
  );

  const finishCrop = () => {
    if (cropperInstance.current) {
      let canvas = cropperInstance.current.getCroppedCanvas();
      if (rounded) {
        canvas = getRoundedCanvas(canvas);
      }
      const regexFileName = /[^\\]*\.(\w+)$/;
      const regexBase64 = /[^:/]\w+(?=;|,)/;
      let [name, ext] = ['', ''];
      if (newFile) {
        [name, ext] = newFile.name.match(regexFileName) as RegExpMatchArray;
      } else if (savedFileUrl) {
        const result = savedFileUrl.match(regexFileName) as RegExpMatchArray;
        if (result) {
          ext = result[1];
        } else {
          ext = savedFileUrl.match(regexBase64)?.[0] || '';
        }
        name = name || 'cropped-file';
      }

      if (!name || !ext) {
        throw new Error('Invalid file.');
      }
      const imageData64 = canvas.toDataURL(`image/${ext}`);
      const newCroppedFile = base64StringtoFile(imageData64, name) as File;
      onCrop(newCroppedFile);
    }
  };

  return (
    <div>
      {fileUrl ? (
        <div
          className={classnames(styles.cropper, { [styles.rounded]: rounded })}
        >
          <img
            id="image"
            className={styles.image}
            src={fileUrl}
            ref={cropperContainer}
            alt="imageToCrop"
          ></img>
          {!isCropperReady ? (
            <LoadingIndicator
              className={styles.loadingIndicator}
              color="blue"
            />
          ) : null}
        </div>
      ) : null}
      <div
        {...getRootProps()}
        className={classnames(styles.uploadSection, 'selectable', {
          [styles.hidden]: fileUrl,
        })}
      >
        <input {...getInputProps()} aria-label="inputAvatar" />
        <img src={uploadFileIcon} alt="uploadFileIcon" />
        <div className={styles.right}>
          <h1>
            Drag and Drop files here, or <span>browse on your computer</span>
          </h1>
          <p className={styles.limitText}>
            Only jpg and png and files are allowed.
          </p>
        </div>
      </div>

      <div
        className={classnames(styles.changePhotoContainer, {
          [styles.hidden]: !fileUrl,
        })}
      >
        <button
          {...getRootProps()}
          type="button"
          className="btn btn-primary-outline chnage-photo-btn"
        >
          Change photo
          <input {...getInputProps()} aria-label="inputAvatar" />
        </button>
      </div>

      <Form.Group className={styles.zoomContainer} controlId="formBasicRange">
        <Form.Label>Zoom</Form.Label>
        <Form.Control
          type="range"
          min={0}
          max={10}
          step={ZOOM_STEP}
          value={zoom}
          onChange={(e) => onZoomChange(Number(e.currentTarget.value))}
        />
      </Form.Group>
    </div>
  );
};

export default Cropper;
